import { useState, useEffect, useCallback } from 'react';
import { X, Download } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * PWA Install Prompt — shows a banner when Chrome/Edge offers installation.
 * Dismissed state is persisted to localStorage so it only shows once per session.
 */
export function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't show if already dismissed this session
    if (sessionStorage.getItem('pwa-prompt-dismissed')) return;
    // Don't show if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
    }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    sessionStorage.setItem('pwa-prompt-dismissed', '1');
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[300] md:bottom-4 md:left-auto md:right-4 md:max-w-sm"
      style={{ paddingBottom: 'calc(56px + env(safe-area-inset-bottom))' }}
      role="dialog"
      aria-label="Install Goblinbox app"
    >
      <div
        className="mx-4 mb-2 md:mx-0 md:mb-0 rounded-xl p-4 flex items-center gap-3 shadow-2xl"
        style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--gb-border)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        }}
      >
        <img
          src="/images/favicon.png"
          alt="Goblinbox icon"
          className="w-12 h-12 rounded-xl shrink-0"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight" style={{ color: 'var(--text-primary)' }}>
            Install Goblinbox
          </p>
          <p className="text-xs mt-0.5 leading-snug" style={{ color: 'var(--text-secondary)' }}>
            Add to your home screen for the best experience
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleInstall}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-opacity hover:opacity-90"
            style={{ background: 'var(--gb-accent)' }}
            aria-label="Install app"
          >
            <Download size={14} />
            Install
          </button>
          <button
            onClick={handleDismiss}
            className="flex items-center justify-center w-7 h-7 rounded-full transition-colors"
            style={{ color: 'var(--text-secondary)' }}
            aria-label="Dismiss install prompt"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
